import React, { useState, useEffect } from 'react';
import { Select, Button, Dialog, Input, MessagePlugin } from 'tdesign-react';
import { useTemplateStore } from '../../../../store/templateStore';
import { isLogin } from '../../../../utils/auth.js';
import './TemplateBar.css';

/**
 * 配置模板栏
 * 显示在参数面板顶部，支持选择模板、保存模板、管理模板
 */
function TemplateBar({ actionName, fields, onApplyTemplate }) {
  const {
    templates,
    currentTemplateName,
    loadTemplatesByAction,
    saveTemplate,
    deleteTemplate,
    selectTemplate,
    getTemplateData,
  } = useTemplateStore();

  const [saveDialogVisible, setSaveDialogVisible] = useState(false);
  const [manageDialogVisible, setManageDialogVisible] = useState(false);
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    if (actionName) {
      loadTemplatesByAction(actionName);
    }
  }, [actionName]);

  // 选择模板并应用
  const handleSelectTemplate = async (name) => {
    const loggedIn = await isLogin();
    if (!loggedIn) return;

    selectTemplate(name);
    const tpl = getTemplateData(name);
    if (tpl && onApplyTemplate) {
      onApplyTemplate(tpl.fields);
    }
  };

  // 打开保存弹窗前校验登录
  const handleOpenSaveDialog = async () => {
    const loggedIn = await isLogin();
    if (!loggedIn) return;
    setSaveDialogVisible(true);
  };

  // 保存为模板
  const handleSave = async () => {
    if (!templateName.trim()) {
      MessagePlugin.warning('请输入模板名称');
      return;
    }
    const result = await saveTemplate(actionName, templateName.trim(), fields);
    if (result.success) {
      MessagePlugin.success('模板保存成功');
      setSaveDialogVisible(false);
      setTemplateName('');
    } else {
      MessagePlugin.warning(result.message);
    }
  };

  // 删除模板
  const handleDelete = async (name) => {
    const result = await deleteTemplate(actionName, name);
    if (result.success) {
      MessagePlugin.success('模板已删除');
    }
  };

  // 管理弹窗中保存当前配置
  const handleSaveFromManage = () => {
    setManageDialogVisible(false);
    setSaveDialogVisible(true);
  };

  const fieldCount = fields?.length || 0;

  return (
    <div className="template-bar">
      <div className="template-bar__selector">
        <span className="template-bar__label">
            配置模板
        </span>
        <Select
          value={currentTemplateName || undefined}
          placeholder="选择模板应用"
          onChange={handleSelectTemplate}
          clearable
          onClear={() => selectTemplate('')}
          style={{ flex: 1 }}
        >
          {templates.map((tpl) => (
            <Select.Option key={tpl.Name} value={tpl.Name} label={tpl.Name} />
          ))}
        </Select>
      </div>
      <div className="template-bar__actions">
        <Button theme="primary" size="small" onClick={handleOpenSaveDialog}>
          存为模板
        </Button>
        <Button theme="default" size="small" variant="outline" onClick={() => {
          loadTemplatesByAction(actionName);
          setManageDialogVisible(true);
        }}>
          管理
        </Button>
      </div>

      {/* 保存模板弹窗 */}
      <Dialog
        visible={saveDialogVisible}
        onClose={() => setSaveDialogVisible(false)}
        header="保存为模板"
        confirmBtn="保存"
        cancelBtn="取消"
        onConfirm={handleSave}
        onCancel={() => setSaveDialogVisible(false)}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ marginBottom: '8px', fontSize: '14px', color: '#333' }}>模板名称</div>
          <Input
            value={templateName}
            onChange={(val) => setTemplateName(val)}
            placeholder="例如：借据-标准评测模板"
          />
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
            将保存当前 {fieldCount} 个字段（含 key / prompt / GT值 / 比对规则）。模板上限 10 个。
          </div>
        </div>
      </Dialog>

      {/* 模板管理弹窗 */}
      <Dialog
        visible={manageDialogVisible}
        onClose={() => setManageDialogVisible(false)}
        header="模板管理"
        footer={false}
      >
        <div className="template-manage">
          <div className="template-manage__header">
            <span className="template-manage__count">已保存 {templates.length} / 10 个模板</span>
            <Button theme="primary" variant="text" size="small" onClick={handleSaveFromManage}>
              + 保存当前配置为新模板
            </Button>
          </div>
          {templates.length === 0 ? (
            <div className="template-manage__empty">
              {/* <div style={{ fontSize: '36px', marginBottom: '12px' }}>📋</div> */}
              <span>暂无模板，点击右上角保存当前配置</span>
            </div>
          ) : (
            <div className="template-manage__list">
              {templates.map((tpl) => {
                let fieldCount = 0;
                try { fieldCount = JSON.parse(tpl.Content)?.length || 0; } catch {}
                return (
                  <div key={tpl.Name} className="template-manage__item">
                    <div className="template-manage__item-info">
                      <span className="template-manage__item-name">{tpl.Name}</span>
                      <span className="template-manage__item-count">{fieldCount} 个字段</span>
                    </div>
                    <div className="template-manage__item-actions">
                      <Button
                        theme="primary"
                        variant="text"
                        size="small"
                        onClick={() => {
                          handleSelectTemplate(tpl.Name);
                          setManageDialogVisible(false);
                        }}
                      >
                        应用
                      </Button>
                      <Button
                        theme="danger"
                        variant="text"
                        size="small"
                        onClick={() => handleDelete(tpl.Name)}
                      >
                        删除
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

export default TemplateBar;
